/**
 * React Query hooks for projects.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  getDeletedProjects,
  restoreProject,
  hardDeleteProject,
  type Project,
  type ProjectsResponse,
} from "../services/naraDoor";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { getCurrentWorkspaceId } from "../lib/workspaceStore";

// Mock data for development mode
const DEV_MOCK_PROJECTS: Project[] = [
  {
    id: "dev-project-1",
    name: "Sample Project",
    description: "A sample project for development",
    workspace_id: "dev-workspace-1",
    pinned: true,
    document_count: 3,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: "dev-project-2",
    name: "Another Project",
    description: "Another sample project",
    workspace_id: "dev-workspace-1",
    pinned: false,
    document_count: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

export function useProjects(options?: { enabled?: boolean }) {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.id;

  return useQuery<ProjectsResponse>({
    queryKey: ["projects", workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        return { projects: [], total: 0 };
      }
      // In dev mode with mock workspace, return mock data
      if (import.meta.env.DEV && workspaceId.startsWith("dev-")) {
        return { projects: DEV_MOCK_PROJECTS, total: DEV_MOCK_PROJECTS.length };
      }
      return getProjects(workspaceId);
    },
    enabled: options?.enabled !== false && !!workspaceId,
  });
}

/**
 * Hook to fetch a single project.
 * @param projectId - The project ID to fetch
 * @param workspaceIdOverride - Optional workspace ID override for admin pages outside workspace context
 */
export function useProject(projectId: string | undefined, workspaceIdOverride?: string) {
  // Use override if provided (for admin routes), otherwise get from store
  const storeWorkspaceId = getCurrentWorkspaceId();
  const workspaceId = workspaceIdOverride || storeWorkspaceId;

  return useQuery<Project>({
    queryKey: ["project", projectId, workspaceId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error("projectId is required");
      }
      // In dev mode with mock project, return mock data
      if (import.meta.env.DEV && projectId.startsWith("dev-")) {
        const project = DEV_MOCK_PROJECTS.find((p) => p.id === projectId);
        if (project) return project;
      }
      // Pass override if provided; otherwise callFunction auto-injects from store
      return getProject(projectId, workspaceIdOverride);
    },
    enabled: !!projectId && !!workspaceId,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      instructions?: string;
      tagIds?: string[];
    }) => {
      if (!activeWorkspace) {
        throw new Error("No active workspace");
      }
      return createProject({
        workspaceId: activeWorkspace.id,
        ...data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", activeWorkspace?.id],
      });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: { name?: string; description?: string; instructions?: string; templateId?: string | null; tagIds?: string[] };
    }) => {
      return updateProject(projectId, data);
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ["project", variables.projectId] });

      // Snapshot previous value for rollback
      const previousProject = queryClient.getQueryData<Project>(["project", variables.projectId, activeWorkspace?.id]);

      // Optimistically update the cache
      if (previousProject) {
        queryClient.setQueryData<Project>(
          ["project", variables.projectId, activeWorkspace?.id],
          { ...previousProject, ...variables.data }
        );
      }

      return { previousProject };
    },
    onError: (_err, variables, context) => {
      // Roll back on error
      if (context?.previousProject) {
        queryClient.setQueryData(
          ["project", variables.projectId, activeWorkspace?.id],
          context.previousProject
        );
      }
    },
    onSettled: (_, _err, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", activeWorkspace?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["project", variables.projectId],
      });
      // Invalidate project-specific shape recipes (generate page template list)
      if (variables.data.templateId !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ["projectShapeRecipes", variables.projectId],
        });
      }
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: (projectId: string) => {
      return deleteProject(projectId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", activeWorkspace?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["deletedProjects", activeWorkspace?.id],
      });
    },
  });
}

export function useDeletedProjects() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.id;

  return useQuery<ProjectsResponse>({
    queryKey: ["deletedProjects", workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        return { projects: [], total: 0 };
      }
      return getDeletedProjects(workspaceId);
    },
    enabled: !!workspaceId,
  });
}

export function useRestoreProject() {
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: (projectId: string) => {
      return restoreProject(projectId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", activeWorkspace?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["deletedProjects", activeWorkspace?.id],
      });
    },
  });
}

export function useHardDeleteProject() {
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: (projectId: string) => {
      return hardDeleteProject(projectId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", activeWorkspace?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["deletedProjects", activeWorkspace?.id],
      });
    },
  });
}
